import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    setCurrentWizardStep,
    setWizardStepCompleted,
    resetRouteDetails,
    selectStartPoint,
    selectEndPoint,
    selectViaPoints,
    selectRouteType,
    selectRouteSegments,
    selectWorkingRules,
    selectWorkingNotifs,
    selectRouteDistance,
    selectRouteTime,
    //setWorkingRules,
    setWorkingNotifs,
    setIsMinimized,
    generateRouteSegments,

} from '../../../features/tripcenter/googleRouteMakerSlice';
import { setRouteMaker, setCreatedMessage, setErrorMessage, getLatestRoute } from '../../../features/tripcenter/routeSlice';
import { saveGoogleRoute } from '../../../features/tripcenter/routeApi';

// MUI components
import Typography from '@mui/material/Typography';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Step components
import RouteTypeSelector from './RouteTypeSelector';
import LocationSetup from './LocationSetup';
import RouteBuilder from './RouteBuilder';
import CompactRouteBuilder from './CompactRouteBuilder';
import SegmentDefinition from './SegmentDefinition';
import RouteSummary from './RouteSummary';

// Instead, import the content components
import RouteRules from './RouteRules';
import RouteNotifications from './RouteNotifications';

// Steps definition
const steps = [
    { key: 'routeType', label: 'Route Type' },
    { key: 'locations', label: 'Locations' },
    { key: 'route', label: 'Build Route' },
    { key: 'segments', label: 'Define Segments' },
    { key: 'rules', label: 'Set Rules' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'summary', label: 'Summary' }
];

const RouteBuilderWizard = ({ isInModal = false }) => {
    const dispatch = useDispatch();
    const currentStep = useSelector(state => state.googleRouteMaker.currentWizardStep);
    const isMinimized = useSelector(state => state.googleRouteMaker.isMinimized);
    const wizardStepsCompleted = useSelector(state => state.googleRouteMaker.wizardStepsCompleted);
    const routePath = useSelector(state => state.googleRouteMaker.routePath);

    // Get route data for validation
    const routeType = useSelector(selectRouteType);
    const startPoint = useSelector(selectStartPoint);
    const endPoint = useSelector(selectEndPoint);
    const viaPoints = useSelector(selectViaPoints);
    const routeSegments = useSelector(selectRouteSegments);
    const rules = useSelector(selectWorkingRules);
    const notifications = useSelector(selectWorkingNotifs);
    const routeDistance = useSelector(selectRouteDistance);
    const routeTime = useSelector(selectRouteTime);

    useEffect(() => {
        console.log("RouteBuilderWizard: Getting latest route");
        dispatch(getLatestRoute());
    }, [dispatch]);

    // Local state
    const [isSaving, setIsSaving] = useState(false);

    // Find current step index
    const currentStepIndex = steps.findIndex(step => step.key === currentStep);

    // Update renderStepContent to mark steps as completed when visited
    const renderStepContent = () => {
        switch (currentStep) {
            case 'routeType':
                return <RouteTypeSelector />;

            case 'locations':
                return <LocationSetup />;

            case 'route':
                const hasCalculatedRoute = Boolean(routePath?.length > 0);
                if (isMinimized) {
                    return (<CompactRouteBuilder />);
                }
                return (
                    <RouteBuilder
                        onConfirm={handleRouteConfirm}
                        isRouteCalculated={hasCalculatedRoute}
                    />
                );

            case 'segments':
                return <SegmentDefinition />;

            case 'rules':
                // Mark rules step as completed when visited
                dispatch(setWizardStepCompleted({ step: 'rules', completed: true }));
                return (
                    <RouteRules
                        //existingRules={rules}
                        //onSubmit={handleRulesSubmit}
                        routeLength={routeLength}
                        routeDuration={routeDuration}
                    />
                );

            case 'notifications':
                // Mark notifications step as completed when visited
                dispatch(setWizardStepCompleted({ step: 'notifications', completed: true }));
                return (
                    <RouteNotifications
                        existingNotifications={notifications}
                        onSubmit={handleNotificationsSubmit}
                    />
                );

            case 'summary':
                return <RouteSummary />;

            default:
                return (
                    <Box p={3}>
                        <Typography>Unknown step</Typography>
                    </Box>
                );
        }
    };

    // Update isStepCompleted to handle optional steps
    const isStepCompleted = (stepKey) => {
        if (stepKey === 'summary') {
            // Summary is complete if all required steps are complete
            return ['routeType', 'locations', 'route', 'segments']
                .every(step => wizardStepsCompleted[step]);
        }
        return wizardStepsCompleted[stepKey];
    };

    // Update canProceedToNext to always allow proceeding from optional steps
    const canProceedToNext = () => {
        // For rules and notifications steps, always allow proceeding
        if (currentStep === 'rules' || currentStep === 'notifications') {
            return true;
        }

        // For summary step, check only required steps
        if (currentStep === 'summary') {
            return ['routeType', 'locations', 'route', 'segments']
                .every(step => wizardStepsCompleted[step]);
        }

        // For all other steps, check completion status
        return isStepCompleted(currentStep);
    };

    // Handle route confirmation
    const handleRouteConfirm = () => {

        // First mark the step as completed
        dispatch(setWizardStepCompleted({ step: 'route', completed: true }));

        // Then generate segments
        if (routePath && routePath.length > 0) {
            dispatch(generateRouteSegments());
        }

        // Finally update step and minimize state
        dispatch(setCurrentWizardStep('segments'));
        dispatch(setIsMinimized(false));
    };

    // Navigate to next step
    const handleNext = () => {
        if (currentStepIndex < steps.length - 1) {
            const nextStep = steps[currentStepIndex + 1].key;

            // If moving to route step, minimize first
            if (currentStep === 'locations') {
                dispatch(setIsMinimized(true));
            }

            dispatch(setCurrentWizardStep(nextStep));
        }
    };

    // Navigate to previous step
    const handleBack = () => {
        if (currentStepIndex > 0) {
            const prevStep = steps[currentStepIndex - 1].key;
            dispatch(setCurrentWizardStep(prevStep));

            // Only dispatch minimize if we're moving to route step
            if (prevStep === 'route') {
                dispatch(setIsMinimized(true));
            }
        }
    };

    // Add route length and duration for rules
    const routeLength = routeDistance / 1000; // Convert to km
    const routeDuration = routeTime / 3600; // Convert to hours

    // Handle save route
    const handleSaveRoute = async () => {
        try {
            setIsSaving(true);

            // Prepare route data for API
            const routeData = {
                routeType,
                startPoint,
                endPoint,
                viaPoints,
                routePath,
                routeDistance,
                routeTime,
                segments: routeSegments,
                rules,
                notifications
            };

            // Call the API to save the route
            console.log("RouteBuilderWizard: Saving route", routeData);
            await dispatch(saveGoogleRoute(routeData)).unwrap();

            // Show success message
            dispatch(setCreatedMessage('Route created successfully'));

            // Reset and close
            dispatch(resetRouteDetails());
            dispatch(setRouteMaker(null));
        } catch (error) {
            console.error('Error saving route:', error);
            dispatch(setErrorMessage('Failed to save route: ' + (error.message || 'Unknown error')));
        } finally {
            setIsSaving(false);
        }
    };

    /* // Handle rules submission
    const handleRulesSubmit = (rulesData) => {
        // Store the rules in Redux
        dispatch(setWorkingRules(rulesData));
        dispatch(setWizardStepCompleted({ step: 'rules', completed: true }));
        handleNext(); // Automatically move to next step when rules are saved
    }; */

    // Handle notifications submission
    const handleNotificationsSubmit = (notificationsData) => {
        // Store the notifications in Redux
        dispatch(setWorkingNotifs(notificationsData));
        dispatch(setWizardStepCompleted({ step: 'notifications', completed: true }));
        handleNext(); // Automatically move to next step when notifications are saved
    };

    // Add safety check for currentStep
    if (!currentStep) {
        return null;
    }

    return (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Stepper - Fixed at the top */}
            <Box sx={{ p: 2, pb: 1 }}>
                <Stepper activeStep={currentStepIndex} alternativeLabel>
                    {steps.map((step, index) => (
                        <Step key={step.key} completed={isStepCompleted(step.key)}>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>

            <Divider />

            {/* Scrollable content area */}
            <Box sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                pb: 10, // Add padding at the bottom to ensure content isn't hidden behind the navigation buttons
            }}>
                {renderStepContent()}
            </Box>

            {/* Fixed navigation buttons at the bottom */}
            <Box sx={{
                position: 'sticky',
                bottom: 0,
                left: 0,
                right: 0,
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                bgcolor: 'background.paper',
                borderTop: '1px solid',
                borderColor: 'divider',
                zIndex: 1
            }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    disabled={currentStepIndex === 0 || isSaving}
                >
                    Back
                </Button>

                <Box>
                    {currentStep === 'summary' ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveRoute}
                            disabled={!isStepCompleted('summary') || isSaving}
                            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {isSaving ? 'Saving...' : 'Save Route'}
                        </Button>
                    ) : (
                        <Button
                            endIcon={<ArrowForwardIcon />}
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                            disabled={!canProceedToNext() || isSaving}
                        >
                            Next
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default RouteBuilderWizard; 